import { kea } from 'kea'
import PropTypes from 'prop-types'

import railsContext from 'lib/selectors/rails-context'
import { removeLanguageUrlFromPath } from 'lib/utils/links'
import offlineToggleModeLogic from 'lib/components/offline-toggle-mode/logic'

export default kea({
  initScene: {
    scene: 'search',

    transform: payload => ({
      results: payload.results,
      filter: payload.filter,
      options: payload.options,
      countries: payload.countries,
      categorySuggestions: payload.categorySuggestions,
      favourites: payload.favourites,
      searchPathCache:
        payload.results.path !== payload.results.search_path
          ? {
              [removeLanguageUrlFromPath(payload.results.path)]: removeLanguageUrlFromPath(payload.results.search_path)
            }
          : {}
    })
  },

  path: () => ['scenes', 'search', 'index'],

  connect: {
    props: [
      offlineToggleModeLogic,
      ['offlineToggleMode'],
      railsContext,
      ['i18nLocale as locale', 'currentUserId as currentUser', 'dpr', 'headerProps']
    ]
  },

  actions: () => ({
    searchRequest: ({ params, meta = {} }) => ({
      params,
      meta
    }),
    receiveResults: (results, filter, meta = {}) => ({ results, filter, meta }),
    translateResultRequest: (resultId, locale) => ({ resultId, locale }),
    translateResultSuccess: result => ({ result }),
    setIsLandingPageUrl: isLandingPageUrl => ({ isLandingPageUrl }),
    hoverLocation: location => ({ location }),
    hoverResult: result => ({ result }),
    clearFilters: true,
    focusResult: id => ({ id }),
    setOpenFilters: filters => ({ filters }),
    updateFilter: (filterChanges, meta = {}) => ({ filterChanges, meta }),
    setNewFilter: (filter, meta = {}) => ({ filter, meta }),
    triggerLocationChange: location => ({ location }),

    receiveFavourites: favourites => ({ favourites }),
    silentGeolocate: true,

    geolocate: true,
    geolocateDone: true,

    setGeolocating: true,
    clearGeolocating: true,

    setLoadingDebounced: true,

    updateSearchPathCache: update => ({ update }),
    setResultsHeight: height => ({ height })
  }),

  dimensions: {
    isSmallScreen: [false, window => window.innerWidth < 640],
    isExtraSmallScreen: [false, window => window.innerWidth <= 480],
    isLargeScreen: [true, window => window.innerWidth >= 960]
  },

  reducers: ({ actions }) => ({
    isLandingPageUrl: [
      true,
      PropTypes.bool,
      {
        [actions.setIsLandingPageUrl]: (_, payload) => payload.isLandingPageUrl
      }
    ],

    countries: [[], {}],

    results: [
      {},
      PropTypes.object,
      {
        [actions.receiveResults]: (state, payload) => payload.results,
        [actions.translateResultSuccess]: (state, payload) => {
          const { result } = payload
          return Object.assign({}, state, {
            results: state.results.map(r => (r.id === result.id ? Object.assign({}, r, result) : r))
          })
        }
      }
    ],

    filter: [
      {},
      PropTypes.object,
      {
        [actions.setNewFilter]: (_, { filter }) => filter,
        [actions.clearFilters]: state => ({ ...state, filters: '' }),
        [actions.setOpenFilters]: (state, { filters }) => ({ ...state, filters }),
        [actions.receiveResults]: (state, { filter: { location } }) => {
          if (location) {
            return Object.assign({}, state, { location })
          } else {
            return state
          }
        }
      }
    ],

    options: [{}, PropTypes.object, {}],

    categorySuggestions: [[], PropTypes.array, {}],

    favourites: [
      [],
      PropTypes.array,
      {
        [actions.receiveFavourites]: (_, payload) => payload.favourites
      }
    ],

    // keeps a cache like:
    // { "/cours-piano/Belgique": "/s/Belgique/Cours-de-Piano/50.495,4.4604/7/25/1/" }
    searchPathCache: [
      {},
      PropTypes.object,
      {
        [actions.receiveResults]: (state, payload) => {
          if (payload.results.search_path !== payload.results.path) {
            const shortPath = removeLanguageUrlFromPath(payload.results.path)
            const longPath = removeLanguageUrlFromPath(payload.results.search_path)
            return { ...state, [shortPath]: longPath }
          } else {
            return state
          }
        },
        [actions.updateSearchPathCache]: (state, payload) => ({ ...state, ...payload.update })
      }
    ],

    hoverResultId: [
      null,
      PropTypes.number,
      {
        [actions.hoverResult]: (_, payload) => (payload.result ? payload.result.id : null)
      }
    ],

    hoverLocationId: [
      null,
      PropTypes.number,
      {
        [actions.hoverLocation]: (_, payload) => (payload.location ? payload.location.id : null)
      }
    ],

    isLoadingResultsRaw: [
      false,
      PropTypes.bool,
      {
        [actions.searchRequest]: () => true,
        [actions.receiveResults]: () => false
      }
    ],

    isLoadingResults: [
      // debounced
      false,
      PropTypes.bool,
      {
        [actions.setLoadingDebounced]: () => true,
        [actions.receiveResults]: () => false
      }
    ],

    isGeolocating: [
      false,
      PropTypes.bool,
      {
        [actions.setGeolocating]: () => true,
        [actions.clearGeolocating]: () => false
      }
    ],

    performedGeolocate: [
      false,
      PropTypes.bool,
      {
        [actions.geolocateDone]: () => true
      }
    ],

    resultsHeight: [
      0,
      PropTypes.number,
      {
        [actions.setResultsHeight]: (_, { height }) => height
      }
    ]
  }),

  selectors: ({ selectors }) => ({
    isFullScreenFiltersPage: [
      () => [selectors.filter, selectors.isSmallScreen],
      ({ filters }, isSmallScreen) => {
        if (!isSmallScreen || !filters) {
          return false
        }
        const fullScreenFiltersList = ['age', 'price', 'location', 'all', 'category_tags']
        let isExist = false
        fullScreenFiltersList.forEach(filterItem => {
          if (isSmallScreen && filters && filters.includes(filterItem)) {
            isExist = true
          }
        })
        return isExist
      },
      PropTypes.bool
    ],

    isFullScreenMap: [
      () => [selectors.filter, selectors.isLargeScreen],
      ({ filters }, isLargeScreen) => !isLargeScreen && !!filters && filters.indexOf('map') > -1,
      PropTypes.bool
    ],

    startMessage: [() => [selectors.results], results => results.start_message || {}, PropTypes.object],
    isCityOrWebcamActivity: [() => [selectors.results], results => results.is_city_or_webcam_activity, PropTypes.bool],

    cleanContentAfterReviews: [
      () => [selectors.startMessage],
      startMessage => {
        const content = startMessage.content || {}
        if (!content.afterReviews) {
          return []
        }

        return content.afterReviews
          .filter(afterReviewContent => afterReviewContent.contentType !== 'faq')
          .map((afterReviewContent, index) => ({
            ...afterReviewContent,
            isTitle: afterReviewContent.contentType === 'full_width_no_bottom',
            isPreviousTitle: index !== 0 && content.afterReviews[index - 1].contentType === 'full_width_no_bottom'
          }))
      },
      PropTypes.array
    ],
    cleanContentBeforeReviews: [
      () => [selectors.startMessage],
      startMessage => {
        const content = startMessage.content || {}
        if (!content.beforeReviews) {
          return []
        }

        return content.beforeReviews
          .filter(beforeReviewContent => beforeReviewContent.contentType !== 'faq')
          .map((beforeReviewContent, index) => ({
            ...beforeReviewContent,
            isTitle: beforeReviewContent.contentType === 'full_width_no_bottom',
            isPreviousTitle: index !== 0 && content.beforeReviews[index - 1].contentType === 'full_width_no_bottom'
          }))
      },
      PropTypes.array
    ],
    searchPageFaqs: [
      () => [selectors.startMessage],
      ({ content }) => {
        if (!content || !content.afterReviews) {
          return []
        }

        const faqs = content.afterReviews.filter(afterReviewContent => afterReviewContent.contentType === 'faq')[0]
        return faqs && faqs.contents ? Object.entries(faqs.contents) : []
      },
      PropTypes.array
    ],
    faqsTitle: [
      () => [selectors.startMessage],
      ({ content }) => {
        if (!content || !content.afterReviews) {
          return ''
        }

        const faqs = content.afterReviews.filter(afterReviewContent => afterReviewContent.contentType === 'faq')[0]
        return faqs && faqs.sectionTitle ? faqs.sectionTitle : ''
      },
      PropTypes.string
    ],
    showStartMessageOnLogOff: [
      () => [
        selectors.filter,
        selectors.isLandingPageUrl,
        selectors.startMessage,
        selectors.currentUser,
        selectors.isLoadingResults
      ],
      (filter, isLandingPageUrl, startMessage, currentUser, isLoadingResults) =>
        !filter.favourites && isLandingPageUrl && startMessage && !currentUser && !isLoadingResults,
      PropTypes.bool
    ],

    showStartMessage: [
      () => [
        selectors.filter,
        selectors.isLandingPageUrl,
        selectors.startMessage,
        selectors.offlineToggleMode,
        selectors.isLoadingResults
      ],
      (filter, isLandingPageUrl, startMessage, offlineToggleMode, isLoadingResults) =>
        !filter.favourites && isLandingPageUrl && startMessage && offlineToggleMode && !isLoadingResults,
      PropTypes.bool
    ],

    isLandingPageOnLogOff: [
      () => [selectors.showStartMessageOnLogOff, selectors.startMessage],
      (showStartMessage, startMessage) => showStartMessage && startMessage.landingPage,
      PropTypes.bool
    ],

    isLandingPage: [
      () => [selectors.showStartMessage, selectors.startMessage],
      (showStartMessage, startMessage) => showStartMessage && startMessage.landingPage,
      PropTypes.bool
    ],

    hasBreadcrumbs: [
      () => [selectors.filter, selectors.isLandingPageUrl, selectors.results, selectors.isSmallScreen],
      (filter, isLandingPageUrl, { breadcrumbs }) => !filter.favourites && breadcrumbs,
      PropTypes.bool
    ],

    showingFavourites: [() => [selectors.filter], filter => filter.favourites, PropTypes.bool],

    showMap: [
      () => [selectors.filter, selectors.showingFavourites],
      (filter, showingFavourites) => filter.location_types !== 'webcam' && !showingFavourites,
      PropTypes.bool
    ],

    searchPathReverseCache: [
      () => [selectors.searchPathCache],
      searchPathCache => {
        const newObject = {}
        Object.entries(searchPathCache).forEach(([key, value]) => {
          newObject[value] = key
        })
        return newObject
      }
    ]
  })
})
